import { Card, CardContent } from '@folio/components/ui/card';
import { CardHeader, CardTitle } from '@folio/components';
import { Button } from '@folio/components/ui/button';
import { SelectCards } from '../../components/forms/select-cards/SelectCards';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { useQuery } from '@tanstack/react-query';
import { legacyDocumentsControllerGetOnDemandDocumentsQueryOptions } from 'korint-sdk';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';

interface OnDemandDocumentGenerationCardProps {
  onGenerate: (configName: string) => void;
  isGenerating?: boolean;
}

export function OnDemandDocumentGenerationCard({
  onGenerate,
  isGenerating = false,
}: OnDemandDocumentGenerationCardProps) {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();
  const [selectedDoc, setSelectedDoc] = useState<string>('');

  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['GENERATE_ON_DEMAND_DOCUMENT', 'GENERATE_ON_DEMAND_POLICYHOLDER_DOCUMENT'],
  });
  // The endpoint rejects callers holding neither action; asking would only log a 403.
  const canGenerateOnDemandDocument =
    can.GENERATE_ON_DEMAND_DOCUMENT || can.GENERATE_ON_DEMAND_POLICYHOLDER_DOCUMENT;

  // The endpoint narrows the list to what the caller's role may generate.
  const { data } = useQuery({
    ...legacyDocumentsControllerGetOnDemandDocumentsQueryOptions({
      productId: policy.productId,
      targetId: policy.id,
      ...(workingBranch?.id ? { branchId: workingBranch.id } : {}),
    }),
    enabled: canGenerateOnDemandDocument,
  });

  const onDemandDocuments = data?.documents ?? [];

  const showGenerateOnDemandGenerationCard =
    onDemandDocuments.length > 0 && policy.status === 'POLICY_STARTED' && !workingBranch;

  if (!showGenerateOnDemandGenerationCard) {
    return null;
  }

  const documentLabel = ({ configName, documentType }: (typeof onDemandDocuments)[number]) =>
    t(`documents.${documentType ?? configName}`);

  const [onlyDocument] = onDemandDocuments;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('documents.generateOnDemand')}</CardTitle>
      </CardHeader>
      <CardContent>
        {onDemandDocuments.length === 1 ? (
          <Group align="stretch" justify="end">
            <Button onClick={() => onGenerate(onlyDocument.configName)} loading={isGenerating}>
              {documentLabel(onlyDocument)}
            </Button>
          </Group>
        ) : (
          <Stack className="w-full">
            <SelectCards
              display="cards"
              maxPerRow={5}
              name={t('documents.generateOnDemand')}
              options={onDemandDocuments.map(doc => ({
                value: doc.configName,
                label: documentLabel(doc),
                icon: FileText,
              }))}
              value={selectedDoc}
              onChange={setSelectedDoc}
              disabled={isGenerating}
            />
            <Button
              className="self-end"
              onClick={() => {
                onGenerate(selectedDoc);
                setSelectedDoc('');
              }}
              disabled={!selectedDoc}
              loading={isGenerating}
            >
              {t('documents.generate')}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
